import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, CreditCard, Clock, Shield, Percent, CalendarDays, PoundSterling } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';

export default function Finance() {
  const financeOptions = [
    {
      title: 'Pay in 3',
      description: 'Split your payment into 3 equal, interest-free instalments',
      icon: CalendarDays,
      details: [
        'No interest charges',
        'Pay over 60 days',
        'First payment at checkout',
        'Automatic monthly payments'
      ],
      example: '£1,350 becomes 3 x £450'
    },
    {
      title: 'Pay in 30 Days',
      description: 'Try now, pay later - full payment after 30 days',
      icon: Clock,
      details: [
        'No upfront payment',
        '30 days to pay in full',
        'No interest if paid on time',
        'Flexible payment date'
      ],
      example: 'Book today, pay in 30 days'
    },
    {
      title: 'Monthly Financing',
      description: 'Spread the cost over 6-36 months',
      icon: PoundSterling,
      details: [
        'Longer payment terms',
        'Fixed monthly payments',
        'Know exactly what you pay',
        'Subject to credit approval'
      ],
      example: '£1,649 from £46/month'
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Choose Your Package',
      description: 'Select any driving course from our packages'
    },
    {
      step: 2,
      title: 'Select Klarna at Checkout',
      description: 'Click "Pay Now" and choose Klarna as your payment method'
    },
    {
      step: 3,
      title: 'Quick Approval',
      description: 'Instant decision - just provide basic details'
    },
    {
      step: 4,
      title: 'Start Learning!',
      description: 'Begin your driving lessons right away'
    }
  ];

  const faqs = [
    {
      question: 'What is Klarna?',
      answer: 'Klarna is a trusted payment provider used by millions worldwide. They offer flexible payment options including "Pay in 3" which splits your purchase into three interest-free instalments.'
    },
    {
      question: 'Is there any interest with Pay in 3?',
      answer: 'No! Pay in 3 is completely interest-free. You simply split your total into 3 equal payments over 60 days.'
    },
    {
      question: 'Will using Klarna affect my credit score?',
      answer: 'Klarna performs a soft credit check which does not affect your credit score. Only if you miss payments could it impact your credit.'
    },
    {
      question: 'How do I pay my instalments?',
      answer: 'Klarna will automatically collect payments from your debit or credit card on the scheduled dates. You can also pay early through the Klarna app.'
    },
    {
      question: 'What if I want to pay off early?',
      answer: 'You can pay off your balance early at any time through the Klarna app with no penalties or extra fees.'
    },
    {
      question: 'Is my payment information secure?',
      answer: 'Absolutely. Klarna uses bank-level encryption and security measures to protect your data. They are regulated by the Financial Conduct Authority (FCA).'
    }
  ];

  return (
    <>
      <SEO 
        title="0% Finance - Pay in 3 Interest-Free | Top Driver Liverpool"
        description="Spread the cost of your driving lessons with 0% finance. Pay in 3 interest-free instalments with Klarna. No hidden fees, instant approval. Start learning to drive today!"
        keywords="driving lessons finance, pay in 3 driving lessons, klarna driving school, interest-free driving lessons liverpool, spread cost driving lessons"
        canonical="https://topdriver.co.uk/finance"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Percent className="w-4 h-4 mr-2" />
            0% Interest Available
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-6">
            Spread the Cost with <span className="text-green-600">0% Finance</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Don't let cost hold you back from learning to drive. With Klarna, you can split your payment into 3 interest-free instalments or choose flexible monthly financing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
            >
              <Link to="/#pricing">View Packages</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-8 py-6 text-lg"
            >
              <a href="#how-it-works">How It Works</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Klarna Logo Section */}
      <section className="py-12 px-4 bg-white border-y border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 mb-4">Powered by</p>
          <img 
            src="https://customer-assets.emergentagent.com/job_dd9d9ce7-fb86-432d-9760-9b14a54b987b/artifacts/qprfr4ue_IMG_1495.png" 
            alt="Klarna" 
            className="h-12 mx-auto mb-4"
          />
          <p className="text-sm text-gray-600">
            Trusted by over 150 million shoppers worldwide
          </p>
        </div>
      </section>

      {/* Finance Options */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
              Choose How You Pay
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Flexible payment options to suit your budget
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {financeOptions.map((option) => (
              <Card key={option.title} className="border-2 border-gray-200 hover:border-green-500 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                    <option.icon className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-black mb-2">{option.title}</h3>
                  <p className="text-gray-600 mb-4">{option.description}</p>
                  
                  <div className="bg-green-50 rounded-lg p-4 mb-6">
                    <p className="text-sm font-semibold text-green-700">{option.example}</p>
                  </div>
                  
                  <ul className="space-y-3">
                    {option.details.map((detail) => (
                      <li key={detail} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Getting started with finance is quick and easy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {howItWorks.map((step, stepIndex) => (
              <div key={step.step} className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-black mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
                {stepIndex < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute right-0 top-8 w-full h-0.5 bg-green-200" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Calculation */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
              See How Much You Could Save
            </h2>
            <p className="text-lg text-gray-600">
              Example: Complete Package (30 Lessons) - £1,350
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-2 border-gray-200">
              <CardContent className="p-8 text-center">
                <h3 className="text-xl font-semibold text-gray-500 mb-4">Pay Full Amount</h3>
                <p className="text-5xl font-bold text-black mb-2">£1,350</p>
                <p className="text-gray-500">One-time payment</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-500 bg-green-50">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold mb-4">
                  RECOMMENDED
                </div>
                <h3 className="text-xl font-semibold text-green-700 mb-4">Pay in 3</h3>
                <p className="text-5xl font-bold text-green-600 mb-2">£450</p>
                <p className="text-green-700 font-medium">x 3 monthly payments</p>
                <p className="text-sm text-green-600 mt-4 font-semibold">0% Interest - No Extra Cost!</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <Shield className="w-10 h-10 text-green-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700">FCA Regulated</p>
            </div>
            <div>
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700">Instant Decision</p>
            </div>
            <div>
              <Percent className="w-10 h-10 text-green-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700">0% Interest</p>
            </div>
            <div>
              <CreditCard className="w-10 h-10 text-green-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700">Secure Payments</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about paying with Klarna
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <Card key={faq.question} className="border border-gray-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-black mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-green-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Start Learning?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Choose any package and select Klarna at checkout to spread the cost
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white hover:bg-gray-100 text-green-600 px-8 py-6 text-lg font-semibold"
          >
            <Link to="/#pricing">View Our Packages</Link>
          </Button>
        </div>
      </section>
    </>
  );
}
